import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { PersonaInput, PersonaExperience } from './buildPersonaString.js';

const ROLES = [
  'Engineer',
  'Product Manager',
  'Designer',
  'Operations',
  'Sales',
  'Marketing',
  'Customer Success',
  'Finance',
  'Legal',
  'People / HR',
  'Executive',
  'Other',
] as const;

const EXPERIENCE_OPTIONS: Array<{ value: PersonaExperience; label: string }> = [
  { value: 'never', label: 'Never used it' },
  { value: 'casual', label: 'Casual user' },
  { value: 'power', label: 'Power user' },
];

type Props = {
  initial?: PersonaInput | null;
  busy?: boolean;
  onSubmit: (input: PersonaInput) => void;
  onClose: () => void;
};

export function PersonaModal({ initial, busy, onSubmit, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { register, handleSubmit, watch } = useForm<PersonaInput>({
    defaultValues: {
      role: initial?.role ?? 'Engineer',
      experience: initial?.experience ?? 'casual',
      goal: initial?.goal ?? '',
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const goalLength = (watch('goal') ?? '').length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="persona-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: '32px 16px',
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 12,
          padding: 36,
          width: '100%',
          maxWidth: 560,
          color: 'var(--color-text)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow" style={{ color: 'var(--color-accent)' }}>▌ Step 1 of 1</div>
        <h2 id="persona-title" style={{ fontSize: 24, fontWeight: 700, margin: '6px 0 6px' }}>
          Tell Claude about you.
        </h2>
        <p style={{ color: 'var(--color-text-3)', fontSize: 13, margin: '0 0 24px' }}>
          Three quick questions. Skip any that don't apply.
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Field label="Your role at Vim">
            <select
              {...register('role', { required: true })}
              style={selectStyle}
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>

          <Field label="How comfortable are you with Notion?">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {EXPERIENCE_OPTIONS.map((opt) => (
                <label key={opt.value} style={radioCardStyle}>
                  <input
                    type="radio"
                    value={opt.value}
                    {...register('experience', { required: true })}
                    style={{ marginRight: 6 }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </Field>

          <Field
            label={
              <span>
                What do you most want to get out of this?{' '}
                <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(optional)</span>
              </span>
            }
          >
            <textarea
              rows={3}
              maxLength={500}
              placeholder="e.g. 'I run weekly team meetings and need a sane place to keep notes' or 'I just want to find docs without getting lost'"
              {...register('goal', { maxLength: 500 })}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
            <div style={{ fontSize: 11, color: 'var(--color-text-3)', marginTop: 4 }}>
              {goalLength} / 500 characters
            </div>
          </Field>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{ background: 'transparent', color: 'var(--color-text-3)', border: 0, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Generating…' : '▸ Generate plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--color-text-2)' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--color-bg-deep)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: 13,
};
const selectStyle = inputStyle;
const radioCardStyle: React.CSSProperties = {
  flex: 1,
  padding: 12,
  background: 'var(--color-bg-deep)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  fontSize: 13,
  textAlign: 'center',
  cursor: 'pointer',
};
